import { Avatar, Loader2, X } from '@chain-zen/ui'

import { PhoneInfoBadge } from '../../../../../components/shared/phone-info-badge'
import type { UserDetails } from '../../../../../types/user'
import type { IWorkspace } from '../../../../../types/workspace'
import { userDisplayName } from '../../../../../utils/user'

interface MemberListItemProps {
  isLoading: boolean
  isSelected: boolean
  memberWorkspace?: IWorkspace
  onRemove: (user: UserDetails) => void
  onSelect: (user: UserDetails) => void
  user: UserDetails
}

/**
 * This component is used to display a single member in the list of members in the workspace.
 * It displays the member's name, email, and photo.
 */
export const MemberListItem = ({
  user,
  isSelected,
  isLoading,
  onRemove,
  onSelect,
  memberWorkspace
}: MemberListItemProps) => {
  const displayName = userDisplayName({
    email: user.email,
    name: user.name,
    phone: user.phone
  })

  return (
    <div
      className={`flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 ${
        isLoading ? 'pointer-events-none opacity-75' : 'cursor-pointer'
      }`}
      onClick={() => {
        if (!isLoading && !isSelected) {
          onSelect(user)
        }
      }}
      data-testid="suggestion"
    >
      <Avatar className="h-8 w-8 shrink-0" src={user.photo} alt={displayName} name={displayName} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1">
          <p className="truncate text-sm font-medium">{user.name}</p>
          {!!memberWorkspace && (
            <p className="truncate text-xs text-muted-foreground">{memberWorkspace?.displayName ?? ''}</p>
          )}
        </div>
        {(!!user.email || !!user.phone) && (
          <p className="truncate text-sm text-muted-foreground">{user.email || `${user.phone}`}</p>
        )}
        {!!user.phoneNumberInfo && <PhoneInfoBadge phoneNumberInfo={user.phoneNumberInfo} />}
      </div>

      <div className="flex items-center shrink-0">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" data-testid="loading" />
        ) : (
          !!isSelected && (
            <button
              type="button"
              className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:hover:bg-red-950/40"
              onClick={(event) => {
                event.stopPropagation()
                onRemove(user)
              }}
              aria-label="Remove member"
            >
              <X className="h-4 w-4" />
            </button>
          )
        )}
      </div>
    </div>
  )
}
